import { ArrowLeft, Search } from "lucide-react";
import type { SowiftAnalysisSessionResponse, SowiftGapAnalysisFindings } from "@/types";

export function GapAnalysisResults({
  session,
  onBack,
}: Readonly<{
  session: SowiftAnalysisSessionResponse;
  onBack: () => void;
}>) {
  const findings = session.findings as unknown as SowiftGapAnalysisFindings;
  const score = findings?.readiness_score ?? 0;

  const getScoreColor = (s: number) => {
    if (s >= 80) return "text-green-400";
    if (s >= 60) return "text-yellow-400";
    if (s >= 40) return "text-orange-400";
    return "text-red-400";
  };

  const getScoreBg = (s: number) => {
    if (s >= 80) return "bg-green-400";
    if (s >= 60) return "bg-yellow-400";
    if (s >= 40) return "bg-orange-400";
    return "bg-red-400";
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case "high":
      case "critical":
        return "bg-red-500/20 text-red-400";
      case "medium":
        return "bg-yellow-500/20 text-yellow-400";
      case "low":
        return "bg-blue-500/20 text-blue-400";
      default:
        return "bg-surface-tertiary/20 text-content-tertiary";
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-content-tertiary hover:text-content-primary transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Analysis
      </button>

      <div className="flex items-center gap-3">
        <Search className="h-5 w-5 text-brand-primary" />
        <h2 className="text-lg font-semibold text-content-primary">Gap Analysis Results</h2>
      </div>

      {/* Readiness Score */}
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <div className="flex items-center gap-6">
          <div className="relative h-20 w-20 shrink-0">
            <svg className="h-20 w-20 transform -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-content-quaternary"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              />
              <path
                className={getScoreColor(score)}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeDasharray={`${score}, 100`}
              />
            </svg>
            <span
              className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${getScoreColor(score)}`}
            >
              {score}
            </span>
          </div>
          <div>
            <h3 className="text-sm font-medium text-content-tertiary">SOW Readiness</h3>
            <p className="text-content-primary mt-1">{findings?.readiness_notes}</p>
          </div>
        </div>
        <div className="mt-4 h-2 bg-surface-tertiary rounded-full overflow-hidden">
          <div
            className={`h-full ${getScoreBg(score)} rounded-full transition-all duration-500`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Summary */}
      {findings?.summary && (
        <div className="bg-surface-primary border border-border-primary rounded-xl p-5">
          <h3 className="text-sm font-medium text-content-tertiary mb-2">Summary</h3>
          <p className="text-sm text-content-primary">{findings.summary}</p>
        </div>
      )}

      {/* Gaps */}
      {findings?.gaps && findings.gaps.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-content-tertiary mb-3">Identified Gaps ({findings.gaps.length})</h3>
          <div className="space-y-3">
            {findings.gaps.map((gap, i) => (
              <div
                key={`gap-${gap.area}-${i}`}
                className="bg-surface-primary border border-border-primary rounded-xl p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${getSeverityColor(gap.severity)}`}>
                    {gap.severity}
                  </span>
                  <h4 className="text-sm font-medium text-content-primary">{gap.area}</h4>
                </div>
                <p className="text-sm text-content-secondary">{gap.description}</p>
                {gap.questions && gap.questions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border-primary">
                    <p className="text-xs text-content-quaternary mb-1.5">Suggested questions to ask the client:</p>
                    <ul className="space-y-1">
                      {gap.questions.map((q, qi) => (
                        <li
                          key={`gq-${q.slice(0, 30)}-${qi}`}
                          className="text-xs text-content-tertiary flex items-start gap-1.5"
                        >
                          <span className="text-brand-primary mt-0.5">-</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
